import { Button, Form, Input, Select, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { fmtMoney } from "@/utils/money";
import { dateFormat } from "@/utils";
import { DATE_PICKER_FORMAT } from "@/config";
import type { Refund, RefundForm } from "@zhihaoscm/entity";
import { fmtNum } from "@zhihaoscm/utils";
import {
    FILTER_PAY_FLAG,
    ORDER_TYPE,
    RECEIVE_PAYMENT_TYPE,
    RECEIVE_STATE,
    SETTLEMENT_METHOD,
    TRADE_ORDER_STATE
} from "@zhihaoscm/meta";

// beta.13 的类型声明暂未暴露 controls，但运行时仍兼容 Vue 版的无步进配置。
type RefundMoneyInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };

export interface TradeRefundOrderOption {
    order: {
        id: string;
        customerEnterprise?: { name?: string; realName?: string; mobile?: string };
        payAmount?: number;
        refundAmount?: number;
        invoicedAmount?: number;
        ton?: number;
        createdTime?: string;
        productInfo?: { productType?: number; settlementMethod?: number };
    };
    receivePayments?: Array<{ paymentType?: number; state?: number; amount?: number }>;
    pendingRefundAmount?: number;
}

export interface TradeRefundFormPageProps {
    loadOrderOptions?: (keyword?: string) => Promise<TradeRefundOrderOption[]>;
    loadDetail?: (id: number | string) => Promise<Refund>;
    createRefund?: (payload: RefundForm) => Promise<unknown>;
    updateRefund?: (id: number | string, payload: RefundForm) => Promise<unknown>;
}

/** 沿用 Vue 退款表单的已收款订单选择接口参数。 */
export async function loadTradeRefundOrderOptions(keyword = ""): Promise<TradeRefundOrderOption[]> {
    const response = await request.service.get<{ list: TradeRefundOrderOption[] }>("/admin/order/selector", {
        // 仅允许 Vue 退款表单定义的已收款订单状态进入候选列表。
        params: {
            page: 1,
            size: 10000,
            states: [
                TRADE_ORDER_STATE.TO_BE_PICKED_UP.code,
                TRADE_ORDER_STATE.IN_THE_PROCESS_OF_PICKING_UP_THE_GOODS.code,
                TRADE_ORDER_STATE.COMPLETED.code,
                TRADE_ORDER_STATE.CANCELLED.code
            ],
            isFilterPayAmount: FILTER_PAY_FLAG.YES.code,
            ...(keyword.trim() ? { keyword: keyword.trim() } : {})
        }
    });
    return response.data.list || [];
}

/** 沿用 Vue 退款新增接口。 */
export async function createTradeRefund(payload: RefundForm): Promise<unknown> {
    const response = await request.service.post("/admin/refund", payload);
    return response.data;
}

/** 沿用 Vue 退款编辑接口。 */
export async function updateTradeRefund(id: number | string, payload: RefundForm): Promise<unknown> {
    const response = await request.service.put(`/admin/refund/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 退款编辑回填接口。 */
export async function loadTradeRefundFormDetail(id: number | string): Promise<Refund> {
    const response = await request.service.get<Refund>(`/admin/refund/${id}`);
    return response.data;
}

/** 退款新增/编辑表单，保留 Vue 订单余额计算和核心保存字段。 */
export function TradeRefundFormPage({
    loadOrderOptions = loadTradeRefundOrderOptions,
    loadDetail = loadTradeRefundFormDetail,
    createRefund = createTradeRefund,
    updateRefund = updateTradeRefund
}: TradeRefundFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const refundId = new URLSearchParams(location.search).get("id") || "";
    const [orders, setOrders] = useState<TradeRefundOrderOption[]>([]);
    const [form, setForm] = useState<RefundForm>({ orderId: "", refundType: "", amount: 0, remark: "" });
    const [selectedOrderId, setSelectedOrderId] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    /** 按旧 Vue 远程订单选择器查询候选，失败时保留当前表单值。 */
    const searchOrderOptions = async (keyword = "") => {
        try {
            setOrders((await loadOrderOptions(keyword)) || []);
            setErrorMessage(null);
        } catch {
            setErrorMessage("订单选项加载失败");
        }
    };

    useEffect(() => {
        void loadOrderOptions()
            .then(result => setOrders(result || []))
            .catch(() => setErrorMessage("订单选项加载失败"));
    }, [loadOrderOptions]);

    useEffect(() => {
        if (!refundId) return;
        void loadDetail(refundId)
            .then(result => {
                setForm({
                    orderId: result.orderId,
                    refundType: result.refundType,
                    amount: Number(result.amount),
                    remark: result.remark || ""
                });
                setSelectedOrderId(result.orderId);
            })
            .catch(() => setErrorMessage("退款信息加载失败"));
    }, [loadDetail, refundId]);

    const selectedOrder = orders.find(item => item.order.id === selectedOrderId);
    // 沿用 Vue 订单摘要的全款判断：捡漏订单默认全款，其他订单按商品结算方式判断。
    const selectedOrderIsFullPayment =
        selectedOrder?.order.productInfo?.productType === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code ||
        selectedOrder?.order.productInfo?.settlementMethod === SETTLEMENT_METHOD.FULL_PAYMENT.code;
    // 从已支付的订单定金收款单中取出摘要展示金额，不参与退款可退金额计算。
    const depositAmount = selectedOrder?.receivePayments?.find(
        payment =>
            payment.paymentType === RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.code && payment.state === RECEIVE_STATE.PAID.code
    )?.amount;
    const maxRefundAmount = useMemo(() => {
        if (!selectedOrder) return 0;
        return (
            (Number(selectedOrder.order.payAmount) || 0) -
            (Number(selectedOrder.pendingRefundAmount) || 0) -
            (Number(selectedOrder.order.refundAmount) || 0) -
            (Number(selectedOrder.order.invoicedAmount) || 0)
        );
    }, [selectedOrder]);

    const updateField = (key: keyof RefundForm, value: string | number) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const submit = async () => {
        if (!form.orderId) {
            setErrorMessage("请选择订单");
            return;
        }
        if (!form.refundType?.trim()) {
            setErrorMessage("请输入退款类型");
            return;
        }
        if (!form.amount || form.amount < 0) {
            setErrorMessage("请输入有效退款金额");
            return;
        }
        if (form.amount > maxRefundAmount) {
            setErrorMessage("退款金额不能大于已收款金额-退款金额-已开票金额");
            return;
        }
        try {
            const payload: RefundForm = {
                orderId: form.orderId,
                refundType: form.refundType.trim(),
                amount: Number(form.amount),
                ...(form.remark?.trim() ? { remark: form.remark.trim() } : {})
            };
            if (refundId) await updateRefund(refundId, payload);
            else await createRefund(payload);
            notifyOperationSuccess("已提交");
            navigate(-1);
        } catch {
            setErrorMessage("退款保存失败");
        }
    };

    // 沿用 Vue ZhMoneyInput 的金额输入契约：保留两位小数并隐藏步进控件。
    const refundMoneyInputProps: RefundMoneyInputProps = {
        "aria-label": "退款金额",
        controls: false,
        max: maxRefundAmount,
        min: 0,
        placeholder: "请输入退款金额",
        precision: 2,
        suffix: "元",
        value: form.amount || undefined,
        onChange: value => updateField("amount", Number(value || 0))
    };

    return (
        <section aria-label="trade-refund-form-page" className="flex h-full min-h-full flex-col">
            <div className="section-card mb-4">
                <ZhDetailHeader title={refundId ? "修改退款" : "新增退款"} />
            </div>
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            <Form labelAlign="right" onFinish={() => void submit()}>
                <div className="section-card mb-4">
                    <div className="section-title mb-5">关联信息</div>
                    <Form.Item label="关联订单" required>
                        <Select
                            allowClear
                            aria-label="关联订单"
                            classNames={{ popup: { root: "trade-refund-order-dropdown" } }}
                            filterOption={false}
                            options={orders.map(item => ({
                                // 与旧 Vue getLabelText 完全一致，订单日期是区分相似订单的必要字段。
                                label: `${item.order.customerEnterprise?.name || item.order.customerEnterprise?.realName} | ${item.order.customerEnterprise?.mobile} | ${fmtNum(item.order.ton)}吨 | ${item.order.id} | ${dateFormat(item.order.createdTime, DATE_PICKER_FORMAT)}`,
                                value: item.order.id
                            }))}
                            placeholder="请选择订单"
                            showSearch
                            value={form.orderId || undefined}
                            onChange={value => {
                                const nextOrderId = value || "";
                                setSelectedOrderId(nextOrderId);
                                updateField("orderId", nextOrderId);
                            }}
                            onSearch={value => void searchOrderOptions(value)}
                        />
                    </Form.Item>
                    {selectedOrder ? (
                        <ZhGrid className="pb-4">
                            <ZhGridItem>
                                <ZhBaseItem
                                    label="采购账号"
                                    value={`${
                                        selectedOrder.order.customerEnterprise?.name ||
                                        selectedOrder.order.customerEnterprise?.realName ||
                                        "-"
                                    }（${selectedOrder.order.customerEnterprise?.mobile || "-"}）`}
                                />
                            </ZhGridItem>
                            <ZhGridItem>
                                <ZhBaseItem label="已收款金额" value={`${fmtMoney(selectedOrder.order.payAmount)}元`} />
                            </ZhGridItem>
                            {!selectedOrderIsFullPayment ? (
                                <ZhGridItem>
                                    <ZhBaseItem label="定金金额" value={`${fmtMoney(depositAmount)}元`} />
                                </ZhGridItem>
                            ) : null}
                            <ZhGridItem>
                                <ZhBaseItem
                                    label="已退款金额"
                                    value={`${fmtMoney(selectedOrder.order.refundAmount)}元`}
                                />
                            </ZhGridItem>
                        </ZhGrid>
                    ) : null}
                </div>
                <div className="section-card">
                    <div className="section-title mb-5">结算信息</div>
                    <ZhGrid>
                        <ZhGridItem>
                            <Form.Item label="退款类型" required>
                                <Input
                                    aria-label="退款类型"
                                    allowClear
                                    maxLength={25}
                                    placeholder="请输入退款类型"
                                    value={form.refundType}
                                    onChange={event => updateField("refundType", event.target.value)}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="退款金额" required>
                                <ZhInputNumber {...refundMoneyInputProps} />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <ZhGrid>
                        <ZhGridItem span={4}>
                            <Form.Item label="退款说明">
                                <Input.TextArea
                                    aria-label="退款说明"
                                    maxLength={200}
                                    placeholder="请输入退款说明"
                                    rows={3}
                                    showCount
                                    value={form.remark || ""}
                                    onChange={event => updateField("remark", event.target.value)}
                                />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </div>
                <Space className="operation-fixed-btn" size={0}>
                    <Button aria-label="提交" htmlType="submit" type="primary">
                        提交
                    </Button>
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </Form>
        </section>
    );
}

export default TradeRefundFormPage;
